import { Download, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  consolidateItems,
  consolidateProject,
  splitByPurchase,
  summarizeConsolidation,
  type ConsolidatedItem,
} from '@obraplan/core/domain/takeoff/consolidation';
import {
  isHourUnit,
  priceLabor,
  type LaborBudgetSummary,
  type PricedLabor,
} from '@obraplan/core/domain/takeoff/labor-rate';
import {
  materialTotal,
  priceMaterials,
  type MaterialBudgetSummary,
  type PricedMaterial,
} from '@obraplan/core/domain/takeoff/material-price';
import {
  hasImportedSheets,
  type ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/feedback/states';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { cn } from '@/lib/cn';
import { AtualizarPrecos } from './AtualizarPrecos';
import { aptosDaObra, type MedidosDe } from './aptos-da-obra';
import { ComoFunciona } from './ComoFunciona';
import { CustoDaHoraNaCelula } from './CustoDaHora';
import { custoDasHoras, custoDireto } from './custo-da-mao-de-obra';
import { Segmentado } from './Segmentado';
import { baixarCsv, nomeDoArquivo, quantitativoEmCsv } from './exportar-quantitativo';
import { metros } from './formato';
import { PrecoNaCelula } from './PrecoDoMaterial';
import { codigosDosMateriais, fatorDoProdutoDe, reais } from './precos-da-obra';
import {
  comodosDaTipologia,
  fundacaoDaTipologia,
  pileDaTipologia,
  quantitiesOf,
} from './resumo-dos-itens';
import type { TypologyDraft, WorkspaceDraft } from './typology-draft';
import type { CustoDaMaoDeObraNaTela } from './use-custo-da-mao-de-obra';
import { useFotoDosCodigosNovos, type PrecosDaObraNaTela } from './use-precos-da-obra';

/**
 * O quantitativo virado do avesso: **item único por item único**.
 *
 * A tela de cima é organizada como a obra pensa — disciplina, serviço, item. Esta é
 * organizada como a compra pensa: cada material uma vez, com o consumo de cada serviço
 * aberto abaixo dele e o total no fim da linha.
 *
 * As duas leituras precisam existir porque as duas perguntas existem. "Quanto de cimento
 * vai no chapisco" é do engenheiro; "quanto de cimento a obra compra" é do almoxarife, e
 * ninguém consegue responder a segunda somando a primeira de cabeça em 218 itens.
 *
 * O escopo é escolhido na tela, e a diferença entre os dois é grande o bastante para não
 * poder ser implícita: **a tipologia** é o desenho, uma vez; **a obra** é ele multiplicado
 * pelos pavimentos que o repetem, somado a todas as outras tipologias. É o segundo que
 * vira pedido de compra.
 */

type Escopo = 'TIPOLOGIA' | 'OBRA';
type Agrupamento = 'MATERIAL' | 'DISCIPLINA';

/** O preço de cada material da lista, pela chave da consolidação (Fase 29, D7). */
type PrecoPorChave = ReadonlyMap<string, PricedMaterial<ConsolidatedItem>>;

/** O R$/h de cada linha de mão de obra, pela chave da consolidação (Fase 29, etapa 3). */
type CustoPorChave = ReadonlyMap<string, PricedLabor<ConsolidatedItem>>;

/**
 * A célula do preço enquanto ele não está: "…" lendo, "—" quando a leitura falhou. Dizer
 * "preço ainda não buscado" em toda linha antes de a leitura voltar seria acusar a obra de
 * uma falta que ela não tem.
 */
function semLinhaDePreco(precos: PrecosDaObraNaTela): string {
  return precos.precos === undefined && precos.erro === undefined ? '…' : '—';
}

/** Um material dentro de uma disciplina — a parte dele que aquela disciplina consome. */
interface LinhaDaDisciplina {
  readonly item: ConsolidatedItem;
  readonly quantidade: number;
  readonly usos: ConsolidatedItem['usages'];
}

/**
 * A mesma lista, repartida por disciplina.
 *
 * O material que duas disciplinas consomem aparece nas duas, cada uma com a parte dela: é
 * a pergunta de quem compra por etapa ("o que a alvenaria vai pedir?"). A lista por
 * material continua sendo a do pedido — nela o tijolo é uma linha só.
 */
function porDisciplina(
  itens: readonly ConsolidatedItem[],
): readonly { readonly disciplina: string; readonly linhas: readonly LinhaDaDisciplina[] }[] {
  const grupos = new Map<string, LinhaDaDisciplina[]>();
  for (const item of itens) {
    const usosPorDisciplina = new Map<string, ConsolidatedItem['usages'][number][]>();
    for (const uso of item.usages) {
      const lista = usosPorDisciplina.get(uso.disciplineName) ?? [];
      lista.push(uso);
      usosPorDisciplina.set(uso.disciplineName, lista);
    }
    for (const [disciplina, usos] of usosPorDisciplina) {
      const linhas = grupos.get(disciplina) ?? [];
      linhas.push({ item, quantidade: usos.reduce((soma, uso) => soma + uso.quantity, 0), usos });
      grupos.set(disciplina, linhas);
    }
  }
  return [...grupos.entries()].map(([disciplina, linhas]) => ({ disciplina, linhas }));
}

export function Consolidacao({
  catalog,
  workspace,
  draft,
  recorteDe,
  medidosDe,
  precos,
  custo,
  fotoAutomatica = true,
}: {
  /** O catálogo **da tipologia** aberta — o que ela escolheu, com o filtro do topo. */
  catalog: ServiceCatalog;
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  /**
   * O recorte de cada tipologia, para a soma da obra: cada uma entra com o que **ela**
   * escolheu, e não com a escolha da que está aberta na tela.
   */
  recorteDe?: (typology: TypologyDraft) => ServiceCatalog;
  /**
   * O medido de cada serviço em cada tipologia — a base do item calculado (Fase 24,
   * fatia 6). Ausente, nenhum item calcula: a soma segue o digitado e a planilha.
   */
  medidosDe?: MedidosDe;
  /**
   * O preço de material da obra (Fase 29, etapa 2). Ausente, a lista é a de antes do
   * orçamento — só quantidade.
   */
  precos?: PrecosDaObraNaTela;
  /**
   * O custo da mão de obra da obra (Fase 29, etapa 3). Ausente, a mão de obra é a de antes do
   * orçamento — só homem-hora.
   */
  custo?: CustoDaMaoDeObraNaTela;
  /**
   * Buscar sozinho o preço dos códigos sem foto (D5). Ligado na tela; o teste que conta as
   * perguntas ao compras o desliga, para contar só as que ele mesmo fez.
   */
  fotoAutomatica?: boolean;
}) {
  const [escopo, setEscopo] = useState<Escopo>('TIPOLOGIA');
  const [agrupamento, setAgrupamento] = useState<Agrupamento>('MATERIAL');
  const [busca, setBusca] = useState('');

  /**
   * As parcelas da obra — as mesmas que somam e as mesmas que a lista mostra.
   *
   * Só no escopo da obra: na vista da tipologia a lista nem chega a ser montada, e
   * percorrer disciplina × serviço × item de cada tipologia para não usar o resultado é
   * trabalho jogado fora a cada dispatch.
   */
  const parcelas = useMemo(
    // `workspace` inteiro, e não só as tipologias: o multiplicador sai da **estrutura**
    // agora, e acrescentar um pavimento tipo tem de refazer o total da obra. Com a
    // dependência estreita, o número que vira pedido de compra envelhecia calado.
    () => (escopo === 'OBRA' ? aptosDaObra(workspace, recorteDe, medidosDe) : []),
    [workspace, escopo, recorteDe, medidosDe],
  );

  const itens = useMemo(() => {
    if (escopo === 'TIPOLOGIA') {
      return consolidateItems(
        catalog,
        quantitiesOf(
          draft,
          medidosDe?.(draft),
          undefined,
          pileDaTipologia(draft, workspace.pileTypes),
          comodosDaTipologia(workspace, draft),
          fundacaoDaTipologia(workspace, draft),
        ),
      );
    }
    return consolidateProject(catalog, parcelas);
  }, [catalog, parcelas, draft, escopo, medidosDe, workspace]);

  const procurado = busca.trim().toLocaleLowerCase('pt-BR');
  const visiveis = useMemo(
    () =>
      procurado === ''
        ? itens
        : itens.filter((item) =>
            `${item.code ?? ''} ${item.description}`.toLocaleLowerCase('pt-BR').includes(procurado),
          ),
    [itens, procurado],
  );

  // As duas listas respondem a perguntas de gente diferente: material vira pedido de
  // compra e é do almoxarife; mão de obra sai em homem-hora e é do planejamento. Somá-las
  // na mesma tabela poria hora de pedreiro num pedido de fornecedor (RN-166).
  const { materials, labor } = useMemo(() => splitByPurchase(visiveis), [visiveis]);
  const resumo = summarizeConsolidation(materials);

  /**
   * O preço de cada material, e a soma (D15).
   *
   * Sobre a lista **inteira** do escopo, e não sobre a busca: o total do orçamento não pode
   * mudar porque alguém procurou "cimento". A busca só escolhe que linhas aparecem.
   */
  const mapaDePrecos = precos?.precos;
  // O fator do produto (13/10/2026): confirmado uma vez, vale aqui sem ninguém digitá-lo de novo.
  const fatoresDoProduto = precos?.fatoresDoProduto;
  const orcado = useMemo(
    () =>
      mapaDePrecos === undefined
        ? undefined
        : priceMaterials(splitByPurchase(itens).materials, mapaDePrecos, fatoresDoProduto),
    [itens, mapaDePrecos, fatoresDoProduto],
  );
  const precoPorChave: PrecoPorChave | undefined = useMemo(
    () =>
      orcado === undefined
        ? undefined
        : new Map(orcado.rows.map((linha) => [linha.item.key, linha])),
    [orcado],
  );

  /**
   * O R$/h de cada linha de mão de obra, e a soma (RN-399, D15).
   *
   * Sobre a lista inteira do escopo, pela mesma razão do material: a busca escolhe o que
   * aparece, e não o que a obra custa.
   */
  const taxas = custo?.custo?.taxas;
  const maoDeObraDoEscopo = useMemo(() => splitByPurchase(itens).labor, [itens]);
  const custoDaMaoDeObra = useMemo(
    () => (taxas === undefined ? undefined : priceLabor(maoDeObraDoEscopo, taxas)),
    [maoDeObraDoEscopo, taxas],
  );
  const custoPorChave: CustoPorChave | undefined = useMemo(
    () =>
      custoDaMaoDeObra === undefined
        ? undefined
        : new Map(custoDaMaoDeObra.rows.map((linha) => [linha.item.key, linha])),
    [custoDaMaoDeObra],
  );
  /** Material + mão de obra — só quando as duas somas existem, e só se há mão de obra. */
  const direto =
    orcado === undefined || custoDaMaoDeObra === undefined || maoDeObraDoEscopo.length === 0
      ? undefined
      : custoDireto(orcado.summary, custoDaMaoDeObra.summary);

  /**
   * Os códigos que o "Atualizar preços" pergunta: os da **obra**, qualquer que seja o escopo
   * à vista — o preço é do material na obra (D7). Contados só no clique.
   */
  const codigosDaObra = (): readonly string[] =>
    codigosDosMateriais(consolidateProject(catalog, aptosDaObra(workspace, recorteDe, medidosDe)));
  // O código que chegou sem foto — pela importação da planilha, sobretudo — busca o preço
  // sozinho (D5), sem esperar alguém achar o "Atualizar preços".
  useFotoDosCodigosNovos(fotoAutomatica ? precos : undefined, codigosDaObra, workspace);
  const semCodigo = resumo.items - resumo.withCode;
  const horas = labor.reduce((total, item) => total + item.total, 0);
  const temPlanilha = hasImportedSheets(catalog);

  /**
   * O que cada parcela leva para a soma da obra.
   *
   * Contado de verdade, e não deduzido da aba: é `consolidateItems` da própria parcela,
   * com o **mesmo** multiplicador que a soma usa. Antes eram duas contas — a lista dizia
   * "× 12 pavimentos" enquanto a soma usava dez mais dois aptos que a lista não mostrava.
   *
   * Toda tipologia tem parcela — `floorsOf` nunca devolve menos que um —, então a que não
   * lançou nada aparece aqui com zero material e o aviso de que ficou de fora. É a única
   * forma de ver isso, e só serve dito antes de alguém ir procurar o número que sumiu.
   */
  const contribuicoes = useMemo(
    () =>
      escopo !== 'OBRA'
        ? []
        : parcelas.map((parcela) => ({
            chave: parcela.chave,
            nome: parcela.name,
            floors: parcela.floors,
            divergente: parcela.divergente,
            itens: consolidateItems(parcela.catalog ?? catalog, parcela.quantities).length,
          })),
    [catalog, parcelas, escopo],
  );
  const escopoNome = escopo === 'TIPOLOGIA' ? draft.name : 'obra';
  const escopoDoOrcamento = escopo === 'TIPOLOGIA' ? `de ${draft.name}` : 'da obra';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quantitativo consolidado</CardTitle>
        <CardDescription>
          Cada material uma vez, com o que cada serviço consome abaixo dele e o total no fim. É esta
          lista que vira pedido de compra.
        </CardDescription>
        <ComoFunciona>
          Materiais se unem pelo <strong>código de compras</strong>: o mesmo saco de argamassa
          escrito de dois jeitos em dois serviços vira uma linha só. Cada material tem{' '}
          <strong>um preço na obra</strong> (custo direto, sem BDI): o <strong>médio</strong> do
          compras, numa foto com data e período; o <strong>provisório</strong>, para quem não tem
          código ou não teve compra no período; ou o <strong>digitado</strong> por cima do médio,
          com motivo. A mão de obra sai à parte, em homem-hora, porque não se compra hora de
          pedreiro do fornecedor (RN-166); o <strong>R$/h</strong> dela vem da folha — a média da
          função na obra, com encargos; sem ninguém na obra, a do sistema; sem ninguém no sistema, o
          provisório. “Obra inteira” é cada tipologia vezes os pavimentos que a repetem, com o apto
          que diverge contado à parte.
        </ComoFunciona>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Segmentado<Escopo>
            label="Escopo do quantitativo"
            atual={escopo}
            opcoes={[
              { valor: 'TIPOLOGIA', rotulo: draft.name },
              { valor: 'OBRA', rotulo: 'Obra inteira' },
            ]}
            onEscolher={setEscopo}
          />
          <Segmentado<Agrupamento>
            label="Como a lista se organiza"
            atual={agrupamento}
            opcoes={[
              { valor: 'MATERIAL', rotulo: 'Por material' },
              { valor: 'DISCIPLINA', rotulo: 'Por disciplina' },
            ]}
            onEscolher={setAgrupamento}
          />

          <div className="relative min-w-48 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-content-muted)"
              aria-hidden="true"
            />
            <Input
              aria-label="Procurar material"
              placeholder="Procurar material…"
              className="pl-9"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
            />
          </div>

          <Button
            variant="secondary"
            disabled={itens.length === 0}
            onClick={() =>
              baixarCsv(quantitativoEmCsv(itens), nomeDoArquivo(escopoNome, new Date()))
            }
          >
            <Download className="size-4" /> Exportar CSV
          </Button>
        </div>

        <p className="text-xs text-(--color-content-muted)">
          <span className="font-medium text-(--color-content)">
            {escopo === 'TIPOLOGIA'
              ? `O desenho de ${draft.name}, uma vez.`
              : 'Todas as tipologias, cada uma vezes os pavimentos que a repetem.'}
          </span>{' '}
          {resumo.items} {resumo.items === 1 ? 'material' : 'materiais'}
          {resumo.items > 0 &&
            (semCodigo === 0 ? (
              ', todos com código de compras'
            ) : (
              <>
                ,{' '}
                <span className="text-(--color-state-warn)">{semCodigo} sem código de compras</span>
              </>
            ))}
          {resumo.shared > 0 && ` · ${String(resumo.shared)} usados por mais de um serviço`}
          {labor.length > 0 &&
            ` · ${String(labor.length)} ${labor.length === 1 ? 'função' : 'funções'} de mão de obra, ${metros(horas, 1)} HH`}
        </p>

        {(precos !== undefined || custo !== undefined) && (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md border border-(--color-border-subtle) px-3 py-2">
            <div className="flex flex-col gap-0.5">
              {precos !== undefined && (
                <ResumoDoOrcamento
                  escopo={escopoDoOrcamento}
                  resumo={orcado?.summary}
                  lendo={precos.precos === undefined && precos.erro === undefined}
                  {...(precos.erro === undefined ? {} : { erro: precos.erro })}
                />
              )}
              {custo !== undefined && maoDeObraDoEscopo.length > 0 && (
                <ResumoDaMaoDeObra
                  escopo={escopoDoOrcamento}
                  resumo={custoDaMaoDeObra?.summary}
                  lendo={custo.custo === undefined && custo.erro === undefined}
                  {...(custo.erro === undefined ? {} : { erro: custo.erro })}
                />
              )}
              {direto !== undefined && (
                <p className="text-sm" data-custo-direto>
                  <span className="text-(--color-content-muted)">
                    {`Custo direto ${escopoDoOrcamento}: material + mão de obra = `}
                  </span>
                  <span className="tabular font-semibold">{reais(direto.total)}</span>
                  {direto.foraDoTotal > 0 && (
                    <span className="text-(--color-state-warn)">
                      {` · ${String(direto.foraDoTotal)} ${
                        direto.foraDoTotal === 1 ? 'linha fora' : 'linhas fora'
                      } do total`}
                    </span>
                  )}
                </p>
              )}
            </div>
            {precos?.podeEditar === true && (
              <AtualizarPrecos precos={precos} codigos={codigosDaObra} />
            )}
          </div>
        )}

        {/*
          O total da obra é uma multiplicação, e multiplicação escondida é número em que
          ninguém pode confiar. Uma tipologia com o multiplicador errado — ou sem nada
          lançado — sai da conta sem deixar rastro; aqui ela aparece, com o que contribui.

          O que decide é **ter quantidade**, e não ter aba: a tipologia montada dentro do
          sistema não aponta aba nenhuma e entra na soma do mesmo jeito. Dizer "sem aba —
          não entra" era falso justamente no caminho que não usa planilha.

          O apto que diverge sai em linha própria, com o multiplicador dele: é o que
          explica por que a tipologia ao lado conta dez pavimentos, e não doze.
        */}
        {escopo === 'OBRA' && (
          <ul
            aria-label="Parcelas da soma da obra"
            className="flex flex-col divide-y divide-(--color-border-subtle) rounded-md border border-(--color-border-subtle) text-xs"
          >
            {contribuicoes.map(({ chave, nome, floors, divergente, itens: quantos }) => (
              <li
                key={chave}
                className={cn(
                  'flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-3 py-1.5',
                  quantos === 0 && 'text-(--color-state-warn)',
                )}
              >
                <span className="font-medium">
                  {nome}
                  {divergente && (
                    <span className="ml-1.5 font-normal text-(--color-state-info)">diverge</span>
                  )}
                </span>
                {quantos === 0 ? (
                  <span>nada lançado — não entra na soma</span>
                ) : (
                  <span className="tabular text-(--color-content-muted)">
                    {quantos} {quantos === 1 ? 'material' : 'materiais'} × {floors}{' '}
                    {floors === 1 ? 'pavimento' : 'pavimentos'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {itens.length === 0 ? (
          <EmptyState
            title="Nenhum material com quantidade"
            description={
              temPlanilha
                ? 'Nenhum item tem quantidade lançada nem herdada da planilha. Escolha a aba que orça a tipologia, ou lance as quantidades em Itens por serviço.'
                : 'Nenhum item tem quantidade lançada. Abra um serviço acima e informe quanto ele consome de cada material.'
            }
          />
        ) : (
          <>
            {visiveis.length === 0 ? (
              <p className="text-sm text-(--color-content-muted)">
                Nenhum material corresponde à busca.
              </p>
            ) : (
              materials.length > 0 &&
              (agrupamento === 'DISCIPLINA' ? (
                <PorDisciplina
                  itens={materials}
                  {...(precos === undefined
                    ? {}
                    : { precos, ...(precoPorChave === undefined ? {} : { precoPorChave }) })}
                />
              ) : (
                <TableWrapper className="max-h-[36rem] overflow-y-auto rounded-md border border-(--color-border-subtle)">
                  <Table>
                    <TableHeader sticky>
                      <TableRow>
                        <TableHead>Material</TableHead>
                        <TableHead className="w-32">Código</TableHead>
                        <TableHead className="w-16">Un.</TableHead>
                        <TableHead className="w-40 text-right">Quantidade</TableHead>
                        {precos !== undefined && (
                          <>
                            <TableHead className="w-48 text-right">Preço unit.</TableHead>
                            <TableHead className="w-36 text-right">Total</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    {materials.map((item) => (
                      <LinhaDoItem
                        key={item.key}
                        item={item}
                        {...(precos === undefined
                          ? {}
                          : { precos, preco: precoPorChave?.get(item.key) })}
                      />
                    ))}
                  </Table>
                </TableWrapper>
              ))
            )}

            {labor.length > 0 && (
              <MaoDeObra
                itens={labor}
                {...(custo === undefined
                  ? {}
                  : { custo, ...(custoPorChave === undefined ? {} : { custoPorChave }) })}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Um material, e os serviços que o consomem.
 *
 * Cada item é um `tbody` próprio: a primeira linha é o material com o total, e as
 * seguintes são os serviços. Um `tbody` por item é o que deixa a tabela dizer, na
 * estrutura e não só no estilo, que aquelas linhas pertencem àquele material.
 */
function LinhaDoItem({
  item,
  precos,
  preco,
}: {
  item: ConsolidatedItem;
  /** Presente, a linha tem as colunas do preço (Fase 29) — mesmo quando este não tem preço. */
  precos?: PrecosDaObraNaTela;
  preco?: PricedMaterial<ConsolidatedItem> | undefined;
}) {
  const unitario = preco?.resolved.unitPrice;
  return (
    <tbody className="border-b border-(--color-border-subtle) last:border-0">
      <TableRow className="border-0">
        <TableCell className="font-medium">{item.description}</TableCell>
        <TableCell className="font-mono text-xs">{item.code ?? '—'}</TableCell>
        <TableCell className="text-xs">{item.unit ?? '—'}</TableCell>
        <TableCell className="tabular text-right font-semibold">{metros(item.total, 3)}</TableCell>
        {precos !== undefined && (
          <>
            <TableCell className="text-right">
              {preco === undefined ? (
                semLinhaDePreco(precos)
              ) : (
                <PrecoNaCelula
                  material={item}
                  price={preco.price}
                  resolved={preco.resolved}
                  podeEditar={precos.podeEditar}
                  definir={precos.definir}
                  fatorDoProduto={fatorDoProdutoDe(precos.fatoresDoProduto, item)}
                  definirFator={precos.definirFatorDoProduto}
                />
              )}
            </TableCell>
            <TableCell className="tabular text-right font-semibold">
              {preco?.total === undefined ? '—' : reais(preco.total)}
            </TableCell>
          </>
        )}
      </TableRow>

      {item.usages.map((uso) => (
        <TableRow key={uso.serviceKey} className="border-0 hover:bg-transparent">
          <TableCell className="py-1 pl-7 text-xs text-(--color-content-muted)" colSpan={3}>
            {uso.disciplineName} › {uso.serviceName}
          </TableCell>
          <TableCell className="tabular py-1 text-right text-xs text-(--color-content-muted)">
            {metros(uso.quantity, 3)}
          </TableCell>
          {/* A parte do serviço no total — o preço é o mesmo, do material (D7). */}
          {precos !== undefined && (
            <>
              <TableCell className="py-1" />
              <TableCell className="tabular py-1 text-right text-xs text-(--color-content-muted)">
                {unitario === undefined ? '' : reais(materialTotal(uso.quantity, unitario))}
              </TableCell>
            </>
          )}
        </TableRow>
      ))}
    </tbody>
  );
}

/**
 * A soma do orçamento de material, e o que falta nela.
 *
 * O "sem preço" vem junto do total, e nunca abaixo dele numa letra menor: o total sem os
 * materiais sem preço é **menor** do que a obra vai custar, e só serve lido com o aviso.
 */
function ResumoDoOrcamento({
  escopo,
  resumo,
  lendo,
  erro,
}: {
  /** "de Apartamento Tipo" ou "da obra". */
  escopo: string;
  resumo: MaterialBudgetSummary | undefined;
  lendo: boolean;
  erro?: string;
}) {
  if (erro !== undefined) {
    return (
      <p className="text-sm text-(--color-state-warn)">{`Os preços não foram lidos: ${erro}`}</p>
    );
  }
  if (lendo || resumo === undefined) {
    return (
      <p className="text-sm text-(--color-content-muted)" role="status">
        Lendo os preços da obra…
      </p>
    );
  }
  return (
    <p className="text-sm" data-total-do-orcamento>
      <span className="text-(--color-content-muted)">{`Material ${escopo}: `}</span>
      <span className="tabular font-semibold">{reais(resumo.total)}</span>
      {resumo.withoutPrice > 0 && (
        <span className="text-(--color-state-warn)">
          {` · ${String(resumo.withoutPrice)} sem preço — fora do total`}
        </span>
      )}
    </p>
  );
}

/**
 * A soma da mão de obra, e o que falta nela (RN-399).
 *
 * O "sem custo" vem junto do total, como o "sem preço" do material: a função sem salário na
 * folha e sem provisório fica fora da soma, e o total sem ela é menor do que a obra vai custar.
 */
function ResumoDaMaoDeObra({
  escopo,
  resumo,
  lendo,
  erro,
}: {
  escopo: string;
  resumo: LaborBudgetSummary | undefined;
  lendo: boolean;
  erro?: string;
}) {
  if (erro !== undefined) {
    return (
      <p className="text-sm text-(--color-state-warn)">
        {`O custo da mão de obra não foi lido: ${erro}`}
      </p>
    );
  }
  if (lendo || resumo === undefined) {
    return (
      <p className="text-sm text-(--color-content-muted)" role="status">
        Lendo o custo da mão de obra…
      </p>
    );
  }
  return (
    <p className="text-sm" data-total-da-mao-de-obra>
      <span className="text-(--color-content-muted)">{`Mão de obra ${escopo}: `}</span>
      <span className="tabular font-semibold">{reais(resumo.total)}</span>
      {resumo.withoutRate > 0 && (
        <span className="text-(--color-state-warn)">
          {` · ${String(resumo.withoutRate)} sem custo — fora do total`}
        </span>
      )}
    </p>
  );
}

/**
 * A lista por disciplina: um bloco por disciplina, com a contagem no título e a parte de
 * cada material que ela consome. O total do material na obra continua na lista por
 * material — aqui cada número é a fatia daquela disciplina.
 */
function PorDisciplina({
  itens,
  precos,
  precoPorChave,
}: {
  itens: readonly ConsolidatedItem[];
  /** Presentes, cada linha diz o preço do material e o total **da parte da disciplina**. */
  precos?: PrecosDaObraNaTela;
  precoPorChave?: PrecoPorChave;
}) {
  const grupos = useMemo(() => porDisciplina(itens), [itens]);
  const comPreco = precos !== undefined;
  return (
    <TableWrapper className="max-h-[36rem] overflow-y-auto rounded-md border border-(--color-border-subtle)">
      <Table>
        <TableHeader sticky>
          <TableRow>
            <TableHead>Material</TableHead>
            <TableHead className="w-32">Código</TableHead>
            <TableHead className="w-16">Un.</TableHead>
            <TableHead className="w-40 text-right">Quantidade</TableHead>
            {comPreco && (
              <>
                <TableHead className="w-48 text-right">Preço unit.</TableHead>
                <TableHead className="w-36 text-right">Total</TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>
        {grupos.map(({ disciplina, linhas }) => (
          <tbody key={disciplina} className="border-b border-(--color-border-subtle) last:border-0">
            <TableRow className="bg-(--color-surface-muted) hover:bg-(--color-surface-muted)">
              <TableCell
                colSpan={comPreco ? 6 : 4}
                className="py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)"
              >
                {disciplina}
                <span className="tabular font-medium normal-case tracking-normal">
                  {' '}
                  · {linhas.length} {linhas.length === 1 ? 'material' : 'materiais'}
                </span>
              </TableCell>
            </TableRow>
            {linhas.map(({ item, quantidade, usos }) => {
              const preco = precoPorChave?.get(item.key);
              const unitario = preco?.resolved.unitPrice;
              return (
                <TableRow key={item.key} className="border-0">
                  <TableCell>
                    <span className="font-medium">{item.description}</span>
                    <span className="block text-xs text-(--color-content-muted)">
                      {usos.map((uso) => uso.serviceName).join(' · ')}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{item.code ?? '—'}</TableCell>
                  <TableCell className="text-xs">{item.unit ?? '—'}</TableCell>
                  <TableCell className="tabular text-right font-semibold">
                    {metros(quantidade, 3)}
                  </TableCell>
                  {precos !== undefined && (
                    <>
                      <TableCell className="text-right">
                        {preco === undefined ? (
                          semLinhaDePreco(precos)
                        ) : (
                          <PrecoNaCelula
                            material={item}
                            price={preco.price}
                            resolved={preco.resolved}
                            podeEditar={precos.podeEditar}
                            definir={precos.definir}
                            fatorDoProduto={fatorDoProdutoDe(precos.fatoresDoProduto, item)}
                            definirFator={precos.definirFatorDoProduto}
                          />
                        )}
                      </TableCell>
                      <TableCell className="tabular text-right font-semibold">
                        {unitario === undefined ? '—' : reais(materialTotal(quantidade, unitario))}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </tbody>
        ))}
      </Table>
    </TableWrapper>
  );
}

// ── Mão de obra ─────────────────────────────────────────────────────────────────

/**
 * A célula do R$/h enquanto ele não está: "…" lendo, "—" quando a leitura falhou — a mesma
 * régua do preço do material.
 */
function semLinhaDeCusto(custo: CustoDaMaoDeObraNaTela): string {
  return custo.custo === undefined && custo.erro === undefined ? '…' : '—';
}

/**
 * As horas, separadas do material.
 *
 * Sai em **homem-hora** (RN-166): compras não vende hora de pedreiro, e a comparação que
 * interessa é contra a execução, que também é em HH. Por isso esta tabela não tem "código
 * de compras" nem entra no pedido: ela é do planejamento.
 *
 * Com o orçamento (Fase 29, etapa 3), cada linha ganha o **R$/h da função** e o total — horas
 * vezes ele. O R$/h vem da folha, com encargos (D11, D12): a média da função na obra; sem
 * ninguém na obra, a do sistema; sem ninguém no sistema, o provisório. A linha sem função
 * (RN-252) só tem o provisório da descrição, e o aviso pede a função.
 */
function MaoDeObra({
  itens,
  custo,
  custoPorChave,
}: {
  itens: readonly ConsolidatedItem[];
  /** Presente, a tabela tem as colunas do R$/h e do total — mesmo quando a linha não tem custo. */
  custo?: CustoDaMaoDeObraNaTela;
  custoPorChave?: CustoPorChave;
}) {
  const total = itens.reduce((soma, item) => soma + item.total, 0);
  const comCusto = custo !== undefined;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <h3 className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
          Mão de obra
          <span className="tabular font-medium normal-case tracking-normal">
            {' '}
            · {metros(total, 1)} HH
          </span>
        </h3>
        <div className="h-px flex-1 bg-(--color-border-subtle)" aria-hidden="true" />
      </div>
      <p className="text-xs text-(--color-content-muted)">
        {comCusto
          ? 'Em homem-hora, fora do pedido de compra; o R$/h vem da folha, com encargos.'
          : 'Em homem-hora, fora do pedido de compra.'}
      </p>

      <TableWrapper className="max-h-[24rem] overflow-y-auto rounded-md border border-(--color-border-subtle)">
        <Table>
          <TableHeader sticky>
            <TableRow>
              <TableHead>Função</TableHead>
              <TableHead className="w-16">Un.</TableHead>
              <TableHead className="w-40 text-right">Homem-hora</TableHead>
              {comCusto && (
                <>
                  <TableHead className="w-56 text-right">R$/h</TableHead>
                  <TableHead className="w-36 text-right">Total</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          {itens.map((item) => {
            const linha = custoPorChave?.get(item.key);
            const porHora = linha?.rate.perHour;
            return (
              <tbody
                key={item.key}
                className="border-b border-(--color-border-subtle) last:border-0"
              >
                <TableRow className="border-0">
                  <TableCell className="font-medium">{item.description}</TableCell>
                  {/* A unidade que não é hora aparece como é: é ela que o aviso acusa. */}
                  <TableCell className="text-xs">
                    {isHourUnit(item.unit) ? 'HH' : item.unit}
                  </TableCell>
                  <TableCell className="tabular text-right font-semibold">
                    {metros(item.total, 1)}
                  </TableCell>
                  {custo !== undefined && (
                    <>
                      <TableCell className="text-right">
                        {linha === undefined || custo.custo === undefined ? (
                          semLinhaDeCusto(custo)
                        ) : (
                          <CustoDaHoraNaCelula
                            item={item}
                            taxa={linha.rate}
                            custo={custo.custo}
                            podeEditar={custo.podeEditar}
                            definirProvisorio={custo.definirProvisorio}
                          />
                        )}
                      </TableCell>
                      <TableCell className="tabular text-right font-semibold">
                        {linha?.total === undefined ? '—' : reais(linha.total)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
                {item.usages.map((uso) => (
                  <TableRow key={uso.serviceKey} className="border-0 hover:bg-transparent">
                    <TableCell
                      className="py-1 pl-7 text-xs text-(--color-content-muted)"
                      colSpan={2}
                    >
                      {uso.disciplineName} › {uso.serviceName}
                    </TableCell>
                    <TableCell className="tabular py-1 text-right text-xs text-(--color-content-muted)">
                      {metros(uso.quantity, 1)}
                    </TableCell>
                    {/* A parte do serviço no total — o R$/h é o mesmo, da função (D11). */}
                    {comCusto && (
                      <>
                        <TableCell className="py-1" />
                        <TableCell className="tabular py-1 text-right text-xs text-(--color-content-muted)">
                          {porHora === undefined ? '' : reais(custoDasHoras(uso.quantity, porHora))}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </tbody>
            );
          })}
        </Table>
      </TableWrapper>
    </section>
  );
}
